import React, { useState, useEffect } from "react";
import { Building, Save, Loader2, X } from "lucide-react";
import { CompanySection } from "@/components/profile/CompanySection";
import { Button } from "@/components/ui/button";
import { CPVSectionFilter } from "./CPVSectionFilter";
import { CPVSuggestionsFromText } from "./CPVSuggestionsFromText";
import { CountrySection } from "./CountrySection";
import { Form, FormField, FormItem, FormLabel, FormControl, FormDescription, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { CurrencySelect } from "./CurrencySelect";
import { Switch } from "@/components/ui/switch";
import { formatCurrency } from "@/utils/dateFormatters";
import { useTranslation } from "react-i18next";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertCircle } from "lucide-react";

const formSchema = z.object({
  company_name: z.string().optional(),
  notification_email: z.string().email().optional(),
  email_notifications_enabled: z.boolean().optional(),
  preferred_industries: z.array(z.string()),
  preferred_countries: z.array(z.string()).min(1, "validation.atLeastOneCountry"),
  min_price_range: z.union([z.number(), z.null()]).optional(),
  max_price_range: z.union([z.number(), z.null()]).optional(),
  search_keywords: z.union([z.string(), z.null()]).optional(),
  price_filter_enabled: z.boolean().optional(),
  price_filter_currency: z.string().optional()
}).refine(
  (data) => {
    // Validate that either we have at least one industry selected OR we have search keywords
    const hasIndustries = data.preferred_industries.length > 0;
    const hasKeywords = data.search_keywords && data.search_keywords.trim().length > 0;
    
    return hasIndustries || hasKeywords;
  },
  {
    message: "validation.industryOrKeywords",
    path: ["preferred_industries"],
  }
);

type FormValues = z.infer<typeof formSchema>;

interface ProfileFormProps {
  profile: {
    company_name: string;
    notification_email: string;
    email_notifications_enabled: boolean;
    preferred_industries: string[];
    preferred_countries: string[];
    min_price_range?: number | null;
    max_price_range?: number | null;
    search_keywords?: string | null;
    price_filter_enabled?: boolean;
    price_filter_currency?: string;
  };
  onProfileChange: (profile: any) => void;
  onSave: () => Promise<void>;
  saving: boolean;
}

export const ProfileForm = ({
  profile,
  onProfileChange,
  onSave,
  saving
}: ProfileFormProps) => {
  const {
    t
  } = useTranslation("profile");
  const {
    toast
  } = useToast();
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_name: profile.company_name || "",
      notification_email: profile.notification_email || "",
      email_notifications_enabled: profile.email_notifications_enabled,
      preferred_industries: profile.preferred_industries || [],
      preferred_countries: profile.preferred_countries || [],
      min_price_range: profile.min_price_range ?? null,
      max_price_range: profile.max_price_range ?? null,
      search_keywords: profile.search_keywords ?? "",
      price_filter_enabled: profile.price_filter_enabled ?? false,
      price_filter_currency: profile.price_filter_currency || "EUR"
    }
  });

  const [priceFilterEnabled, setPriceFilterEnabled] = useState<boolean>(profile.price_filter_enabled ?? false);

  const validateForm = () => {
    // First check if we have at least one country
    const countries = form.getValues("preferred_countries");
    if (countries.length === 0) {
      setValidationError(t("validation.atLeastOneCountry"));
      setValidationDialogOpen(true);
      return false;
    }
    
    // Then check if we have either industries or keywords
    const industries = form.getValues("preferred_industries");
    const keywords = form.getValues("search_keywords");
    const hasKeywords = keywords && keywords.trim().length > 0;
    
    if (industries.length === 0 && !hasKeywords) {
      setValidationError(t("validation.industryOrKeywords"));
      setValidationDialogOpen(true);
      return false;
    }
    
    return true;
  };

  const handleSave = async () => {
    if (saving) return;
    
    if (!validateForm()) {
      return;
    }
    
    try {
      const formData = form.getValues();
      console.log("Form data:", formData);
      const cleanKeywords = formData.search_keywords?.split(',').map(k => k.trim()).filter(k => k).join(', ');
      const updatedProfile = {
        ...profile,
        company_name: formData.company_name || "",
        notification_email: formData.notification_email || "",
        email_notifications_enabled: formData.email_notifications_enabled,
        min_price_range: priceFilterEnabled ? formData.min_price_range ?? null : null,
        max_price_range: priceFilterEnabled ? formData.max_price_range ?? null : null,
        price_filter_enabled: priceFilterEnabled,
        price_filter_currency: formData.price_filter_currency || "EUR",
        search_keywords: cleanKeywords || null
      };
      
      console.log("Updating profile with:", updatedProfile);
      onProfileChange(updatedProfile);
      await onSave();
      toast({
        title: t("successMessage"),
        description: t("successDescription")
      });
      
    } catch (error) {
      console.error("Error saving profile:", error);
      toast({
        title: t("errorMessage"),
        description: t("errorDescription"),
        variant: "destructive"
      });
    }
  };

  const handleRemoveKeyword = (keywordToRemove: string) => {
    const currentKeywords = form.getValues("search_keywords") ?? "";
    const keywordList = currentKeywords.split(',').map(k => k.trim()).filter(k => k);
    const updatedKeywords = keywordList.filter(k => k !== keywordToRemove).join(', ');
    form.setValue("search_keywords", updatedKeywords);
    onProfileChange({
      ...profile,
      search_keywords: updatedKeywords || null
    });
  };

  const getKeywords = () => {
    const keywords = form.watch("search_keywords");
    return keywords ? keywords.split(',').map(k => k.trim()).filter(k => k) : [];
  };

  const handleCPVSelect = (cpv: string) => {
    const currentCPVs = form.getValues("preferred_industries");
    
    if (!currentCPVs.includes(cpv)) {
      const updatedCPVs = [...currentCPVs, cpv];
      
      form.setValue("preferred_industries", updatedCPVs, { 
        shouldValidate: true,
        shouldDirty: true
      });
      
      onProfileChange({
        ...profile,
        preferred_industries: updatedCPVs
      });
      
      toast({
        title: t("cpvCodes.added"),
        description: t("cpvCodes.addedDescription"),
        duration: 3000,
      });
    }
  };

  useEffect(() => {
    if (!form.getValues("price_filter_currency")) {
      form.setValue("price_filter_currency", "EUR");
    }
  }, [form]);

  return (
    <>
      <Form {...form}>
        <form className="max-w-4xl mx-auto space-y-8">
          <div className="flex items-center gap-4 mb-8">
            <Building className="h-10 w-10 text-primary" />
            <h1 className="text-4xl font-bold text-gray-900">{t("title")}</h1>
          </div>

          <div className="border-2 border-primary/20 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">{t("freeTextSearch.title")}</h2>
            <p className="text-sm text-gray-600 mb-4">
              {t("freeTextSearch.description")}
            </p>
            <FormField control={form.control} name="search_keywords" render={({
            field
          }) => <FormItem>
                  <FormLabel>{t("freeTextSearch.label")}</FormLabel>
                  <FormControl>
                    <Textarea placeholder={t("freeTextSearch.placeholder")} className="min-h-[100px]" {...field} value={field.value ?? ''} onChange={e => {
                field.onChange(e.target.value);
                onProfileChange({
                  ...profile,
                  search_keywords: e.target.value || null
                });
              }} />
                  </FormControl>
                  <FormDescription>
                    {t("freeTextSearch.help")}
                  </FormDescription>
                </FormItem>} />
            {getKeywords().length > 0 && <div className="flex flex-wrap gap-2 mt-4">
                {getKeywords().map((keyword, index) => <Badge key={index} variant="secondary" className="px-3 py-1 cursor-pointer hover:bg-secondary/80 group" onClick={() => handleRemoveKeyword(keyword)}>
                    {keyword}
                    <X className="w-3 h-3 ml-2 inline-block opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Badge>)}
              </div>}
            
            <CPVSuggestionsFromText searchText={form.watch("search_keywords")} existingSelections={profile.preferred_industries || []} onCPVSelect={handleCPVSelect} />
          </div>

          <div className="border-2 border-primary/20 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">{t("priceRange.title")}</h2>
            
            <div className="flex items-center space-x-2 mb-6">
              <Switch id="price-filter-enabled" checked={priceFilterEnabled} onCheckedChange={checked => {
                setPriceFilterEnabled(checked);
                onProfileChange({
                  ...profile,
                  price_filter_enabled: checked
                });
              }} />
              <label htmlFor="price-filter-enabled" className="text-sm font-medium">
                {t("priceRange.enableFiltering")}
              </label>
            </div>

            {priceFilterEnabled && <>
                <div className="mb-4">
                  <FormField control={form.control} name="price_filter_currency" render={({
                field
              }) => <FormItem>
                        <FormLabel>{t("priceRange.currency")}</FormLabel>
                        <FormControl>
                          <CurrencySelect value={field.value || "EUR"} onChange={value => {
                    field.onChange(value);
                    onProfileChange({
                      ...profile,
                      price_filter_currency: value
                    });
                  }} />
                        </FormControl>
                        <FormDescription>
                          {t("priceRange.currency")}
                        </FormDescription>
                      </FormItem>} />
                </div>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-md mb-6">
                  <p className="text-amber-800 text-sm">
                    <strong>{t("errorMessage")}:</strong> {t("priceRange.note")}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField control={form.control} name="min_price_range" render={({
                field
              }) => <FormItem>
                        <FormLabel>{t("priceRange.minValue")}</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder={t("priceRange.minValuePlaceholder")} disabled={!priceFilterEnabled} {...field} onChange={e => {
                    const value = e.target.value ? Number(e.target.value) : null;
                    field.onChange(value);
                    onProfileChange({
                      ...profile,
                      min_price_range: value
                    });
                  }} value={field.value ?? ""} />
                        </FormControl>
                        <FormDescription>
                          {field.value ? formatCurrency(field.value, form.getValues("price_filter_currency") || "EUR") : t("priceRange.noMinValue")}
                        </FormDescription>
                      </FormItem>} />

                  <FormField control={form.control} name="max_price_range" render={({
                field
              }) => <FormItem>
                        <FormLabel>{t("priceRange.maxValue")}</FormLabel>
                        <FormControl>
                          <Input type="number" placeholder={t("priceRange.maxValuePlaceholder")} disabled={!priceFilterEnabled} {...field} onChange={e => {
                    const value = e.target.value ? Number(e.target.value) : null;
                    field.onChange(value);
                    onProfileChange({
                      ...profile,
                      max_price_range: value
                    });
                  }} value={field.value ?? ""} />
                        </FormControl>
                        <FormDescription>
                          {field.value ? formatCurrency(field.value, form.getValues("price_filter_currency") || "EUR") : t("priceRange.noMaxValue")}
                        </FormDescription>
                      </FormItem>} />
                </div>
              </>}
          </div>

          <div className="border-2 border-primary/20 rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">{t("cpvCodes.title")}</h2>
            <p className="text-sm text-gray-600 mb-4">
              {t("cpvCodes.description")}
            </p>
            <FormField
              control={form.control}
              name="preferred_industries"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <CPVSectionFilter 
                      selectedCPVs={field.value || []} 
                      onCPVChange={cpv => {
                        const currentCPVs = field.value || [];
                        const updatedCPVs = currentCPVs.includes(cpv) 
                          ? currentCPVs.filter(c => c !== cpv) 
                          : [...currentCPVs, cpv];
                        field.onChange(updatedCPVs);
                        onProfileChange({
                          ...profile,
                          preferred_industries: updatedCPVs
                        });
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="border-2 border-primary/20 rounded-lg p-6">
            <FormField
              control={form.control}
              name="preferred_countries"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <CountrySection
                      selectedCountries={field.value || []}
                      onCountryChange={country => {
                        const currentCountries = field.value || [];
                        const updatedCountries = currentCountries.includes(country)
                          ? currentCountries.filter(c => c !== country)
                          : [...currentCountries, country];
                        field.onChange(updatedCountries);
                        onProfileChange({
                          ...profile,
                          preferred_countries: updatedCountries
                        });
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex justify-end pt-4">
            <Button type="button" onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-6">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? t("savingChanges") : t("saveChanges")}
            </Button>
          </div>
        </form>
      </Form>

      <AlertDialog open={validationDialogOpen} onOpenChange={setValidationDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-destructive" />
              {t("validation.error")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              {validationError}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>OK</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
